import React from 'react';
import { TrialForm } from '../services/apiService';

/**
 * "Start your free trial" — the details sheet in front of the card-free trial.
 *
 * The trial asks for no card (Sep 2026 spec); what it asks for instead is who
 * the person is: name, organization, designation. Those are the four fields
 * the server requires before it grants access, and they are written to the
 * profile as well as snapshotted on the trial row, so a half-empty profile
 * gets filled by the one form everyone on a trial has to complete.
 *
 * Same shell as PricingModal / CancelTrialModal (backdrop click, ×, Escape,
 * focus in and back out) so the three sheets feel like one product. Nothing
 * here talks to the server: the parent owns the request through `onSubmit`,
 * which answers {ok, error} — the sheet shows the error and stays up, or the
 * parent closes it on success. There is no card step after it: the trial is
 * card-free for everyone (the autopay trial was retired on 2026-09-23).
 */

interface TrialFormModalProps {
  open: boolean;
  /** Prefilled from the profile so someone who has already told us their
   *  organization is not asked to type it twice. */
  initial: Partial<TrialForm>;
  days: number;
  onSubmit: (form: TrialForm) => Promise<{ ok: boolean; error?: string }>;
  onClose: () => void;
}

type Field = keyof TrialForm;

const FIELDS: { key: Field; label: string; placeholder: string; autoComplete: string }[] = [
  { key: 'first_name', label: 'First name', placeholder: 'First name', autoComplete: 'given-name' },
  { key: 'last_name', label: 'Last name', placeholder: 'Last name', autoComplete: 'family-name' },
  { key: 'organization', label: 'Organization', placeholder: 'Company or university', autoComplete: 'organization' },
  { key: 'designation', label: 'Designation', placeholder: 'e.g. Head of Innovation', autoComplete: 'organization-title' },
];

/** The server caps each field at 120 characters; the input stops there too
 *  so the only 400 it can answer is "missing", never "too long". */
const MAX_LEN = 120;

const clean = (v: unknown) => String(v ?? '').trim();

/** Mounted only while open, so every opening starts from `initial` again —
 *  a rejected attempt closed with × does not come back half-typed with the
 *  old error still showing. */
const TrialFormSheet: React.FC<Omit<TrialFormModalProps, 'open'>> = ({
  initial, days, onSubmit, onClose,
}) => {
  const [form, setForm] = React.useState<TrialForm>({
    first_name: clean(initial.first_name),
    last_name: clean(initial.last_name),
    organization: clean(initial.organization),
    designation: clean(initial.designation),
  });
  const [missing, setMissing] = React.useState<Partial<Record<Field, boolean>>>({});
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState('');

  // Closing mid-request is ignored rather than cancelling it: the server may
  // already have started the trial, and a sheet that vanished before the
  // answer came back would leave the page showing no trial at all.
  const close = () => { if (!busy) onClose(); };
  const firstRef = React.useRef<HTMLInputElement>(null);
  const closeFnRef = React.useRef(close);
  closeFnRef.current = close;
  React.useEffect(() => {
    const previously = document.activeElement as HTMLElement | null;
    // Focus lands on the first EMPTY field: a prefilled name puts the cursor
    // where typing is actually needed.
    const firstEmpty = FIELDS.find(f => !form[f.key])?.key;
    const el = firstEmpty
      ? document.getElementById(`trial-form-${firstEmpty}`) as HTMLInputElement | null
      : firstRef.current;
    (el || firstRef.current)?.focus();
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') closeFnRef.current(); };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      previously?.focus?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (k: Field, v: string) => {
    setForm(f => ({ ...f, [k]: v }));
    // A field being typed into is no longer missing; the error line is the
    // server's answer to the LAST attempt and clears with the next edit too.
    if (missing[k]) setMissing(m => ({ ...m, [k]: false }));
    if (error) setError('');
  };

  const submit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (busy) return;
    const trimmed: TrialForm = {
      first_name: clean(form.first_name), last_name: clean(form.last_name),
      organization: clean(form.organization), designation: clean(form.designation),
    };
    const gaps: Partial<Record<Field, boolean>> = {};
    FIELDS.forEach(f => { if (!trimmed[f.key]) gaps[f.key] = true; });
    if (Object.keys(gaps).length) {
      setMissing(gaps);
      setError('');
      document.getElementById(`trial-form-${FIELDS.find(f => gaps[f.key])!.key}`)?.focus();
      return;
    }
    setBusy(true); setError('');
    const res = await onSubmit(trimmed);
    // On success the parent unmounts this sheet; setting state after that
    // would only warn, so nothing more is done here.
    if (!res.ok) {
      setBusy(false);
      setError(res.error || 'Could not start the trial. Please try again.');
    }
  };

  const inputClass = (k: Field) =>
    `h-11 px-3 rounded-lg border bg-white text-sm w-full outline-none transition-colors ${
      missing[k] ? 'border-red-400 focus:border-red-500'
                 : 'border-grayscale-300 focus:border-primary-500'}`;

  const field = (f: typeof FIELDS[number]) => (
    <div key={f.key} className="flex flex-col gap-1 min-w-0">
      <label htmlFor={`trial-form-${f.key}`} className="text-xs font-medium text-grayscale-1200">
        {f.label}
      </label>
      <input
        id={`trial-form-${f.key}`}
        ref={f.key === 'first_name' ? firstRef : undefined}
        value={form[f.key]}
        onChange={e => set(f.key, e.target.value)}
        placeholder={f.placeholder}
        autoComplete={f.autoComplete}
        maxLength={MAX_LEN}
        disabled={busy}
        aria-invalid={!!missing[f.key]}
        aria-describedby={missing[f.key] ? `trial-form-${f.key}-error` : undefined}
        className={inputClass(f.key)} />
      {missing[f.key] && (
        <span id={`trial-form-${f.key}-error`} className="text-[11px] text-red-600">
          {f.label} is required.
        </span>
      )}
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4"
         onClick={close}>
      <div role="dialog" aria-modal="true" aria-labelledby="trial-form-title"
           className="relative bg-white rounded-2xl shadow-lg max-w-md w-full max-h-[90vh] flex flex-col"
           onClick={e => e.stopPropagation()}>
        <button type="button" onClick={close} aria-label="Close" disabled={busy}
          className="absolute top-4 right-4 z-10 w-8 h-8 rounded-full text-grayscale-1100 hover:bg-grayscale-200 text-xl leading-none disabled:opacity-40">
          ×
        </button>
        <form className="p-5 sm:p-6 overflow-y-auto" onSubmit={submit} noValidate>
          {/* The rocket tile: the same 40px icon tile PricingModal opens
              with, so the sheet reads as the next step of that modal rather
              than a different product. */}
          <div className="w-10 h-10 rounded-xl bg-primary-100/60 flex items-center justify-center mb-3">
            <svg className="w-5 h-5 text-primary-700" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M14.5 4.5c2.5-1 5-1 5 0s0 2.5-1 5c-1.2 3-3.5 5.6-6 7.5l-3.5-3.5c1.9-2.5 4.5-4.8 5.5-9z"
                    stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
              <path d="M9 13.5 5.5 12l2.2-2.2M10.5 15 12 18.5l2.2-2.2M6.5 17.5 4 20"
                    stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              <circle cx="14.5" cy="9.5" r="1.2" fill="currentColor" />
            </svg>
          </div>
          <div className="text-[11px] font-bold tracking-widest text-primary-700 uppercase mb-1">
            {days}-day free trial
          </div>
          <h2 id="trial-form-title" className="text-xl font-bold text-grayscale-1300 font-grotesk pr-8">
            Start your free trial
          </h2>
          <p className="text-[13px] text-grayscale-1200 mt-1 mb-4">
            Tell us a bit about yourself to get started. No card required.
          </p>

          <div className="flex flex-col gap-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {field(FIELDS[0])}
              {field(FIELDS[1])}
            </div>
            {field(FIELDS[2])}
            {field(FIELDS[3])}
          </div>

          {error && (
            <div role="alert" className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mt-3">
              {error}
            </div>
          )}

          <button type="submit" disabled={busy}
            className="mt-4 w-full py-3 px-5 rounded-xl font-grotesk text-sm font-semibold bg-primary-600 text-grayscale-100 hover:bg-primary-700 transition-colors disabled:bg-primary-100 disabled:text-primary-300">
            {busy ? 'Starting…' : 'Start trial'}
          </button>
        </form>
      </div>
    </div>
  );
};

const TrialFormModal: React.FC<TrialFormModalProps> = ({ open, ...rest }) =>
  open ? <TrialFormSheet {...rest} /> : null;

export default TrialFormModal;
